import { afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, effect, inject, Injector, signal, viewChild } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AssetFileInputComponent } from '../../../../shared/components/asset-file-input/asset-file-input.component';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../../@core/services/notification/notification.service';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { Asset, JournalAttachedEntityType, Product, ProductInput, TaskAttachedEntityType } from '../../../../generated/generated-types';
import { AssetService } from '../../../asset/services/asset.service';
import { WidgetAssetsManagerComponent } from '../../../asset/widget-assets-manager/widget-assets-manager.component';
import { JournalListWidgetComponent } from '../../../journal/components/journal-list-widget/journal-list-widget.component';
import { TaskListWidgetComponent } from '../../../task/components/task-list-widget/task-list-widget.component';
import { ProductService } from '../../services/product.service';
import { WidgetAssetListComponent } from '../widget-asset-list/widget-asset-list.component';

@Component({
  selector: 'app-widget-product-assets-manager',
  templateUrl: './widget-product-assets-manager.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    WidgetAssetListComponent,
    AssetFileInputComponent,
    BlockableDiv,
    TranslatePipe,
    ButtonModule,
    TooltipModule,
    JournalListWidgetComponent,
    TaskListWidgetComponent,
  ]
})
export class WidgetProductAssetsManagerComponent extends WidgetAssetsManagerComponent<Product> {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  protected destroyRef = inject(DestroyRef);

  private readonly journalListWidget = viewChild<JournalListWidgetComponent>('journalListWidget');
  private readonly taskListWidget = viewChild<TaskListWidgetComponent>('taskListWidget');

  isSaving = signal<boolean>(false);

  /** Last entity id we applied journal/task filters for — prevents refresh loops. */
  private attachedFiltersAppliedForId: string | null = null;

  get product() {
    return this.model as Product;
  }

  get isBlocked(): boolean {
    return !this.model?.id;
  }

  /** Viewport fill only while blocked — never tab fillHeight (that kills page scroll). */
  get blockableClass(): string {
    return this.isBlocked
      ? 'flex min-h-0 w-full flex-col'
      : 'w-full';
  }

  get blockedStyle(): Record<string, string> {
    return this.isBlocked
      ? { height: 'calc(100vh - 14rem)', minHeight: 'calc(100vh - 14rem)' }
      : {};
  }

  constructor(
    public productService: ProductService,
    public assetService: AssetService,
    public notificationService: NotificationService,
    public i18n: I18nService,
  ) {
    super(productService, assetService, notificationService, i18n);

    // Apply once when list hosts appear — guarded so refresh cannot loop.
    effect(() => {
      const journal = this.journalListWidget();
      const task = this.taskListWidget();
      if (!journal && !task) {
        return;
      }
      this.applyAttachedEntityFilters();
    });

    afterNextRender(() => {
      this.applyAttachedEntityFilters();
    }, { injector: this.injector });
  }

  setSelectedItemAsFeature = async () => {
    const selectedItems: Asset[] = this.assetListWidget().selectedItems();

    if (selectedItems.length > 0) {
      const updateProd: ProductInput = {
        id: this.model.id,
        featuredAssetId: selectedItems[0].id
      };
      await this.productService.saveItem(updateProd);
      this.notificationService.success(_("common.save-successful"));
    }
  };

  checkIfCanSave() {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Product>;
    return component.detailForm.invalid;
  }

  async saveToUnlockEditMode() {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Product>;
    if (!component) {
      console.error("No detail component found to save Asset");
      return;
    }

    this.isSaving.set(true);
    this.cdRef.detectChanges();

    await component.save();

    this.isSaving.set(false);
    this.attachedFiltersAppliedForId = null;
    this.cdRef.detectChanges();

    afterNextRender(() => {
      this.applyAttachedEntityFilters();
    }, { injector: this.injector });
  }

  private applyAttachedEntityFilters(): void {
    const productId = this.product?.id;
    if (!productId) {
      return;
    }

    const journal = this.journalListWidget();
    const task = this.taskListWidget();
    if (!journal && !task) {
      return;
    }

    if (this.attachedFiltersAppliedForId === productId) {
      return;
    }

    journal?.setCustomSearchInput({
      attachedEntityType: JournalAttachedEntityType.PRODUCT,
      attachedEntityId: productId,
    });

    task?.setCustomSearchInput({
      attachedEntityType: TaskAttachedEntityType.PRODUCT,
      attachedEntityId: productId,
    });

    this.attachedFiltersAppliedForId = productId;
  }
}
